import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { getMyTrip } from "@/lib/trip.functions";
import { getRecentTripMediaForAi, reanalyzeMedia } from "@/lib/media.functions";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/admin/PageHeader";
import { AiControlCenter } from "@/components/admin/AiControlCenter";
import { AdminAiAdvancedControls } from "@/components/admin/AdminAiAdvancedControls";
import { AiAdminTabs, type AiTabKey } from "@/components/admin/ai/AiAdminTabs";
import { AiOverviewSection } from "@/components/admin/ai/AiOverviewSection";
import { RoavaCard, StatusChip, SecondaryButton, type StatusChipVariant } from "@/components/roava";
import { pageMeta } from "@/lib/seo";
import { toast } from "sonner";
import { RefreshCw, Wand2, Copy, ChevronDown, ImageOff, Film, AlertTriangle, Star } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export const Route = createFileRoute("/_authenticated/admin/ai")({
  head: () =>
    pageMeta({
      title: "AI photo analysis debug — Roava admin",
      description: "Admin-only verification of AI photo analysis status.",
      path: "/admin/ai",
    }),
  component: AiDebugPage,
});

const VALID_TABS: AiTabKey[] = ["overview", "run", "providers", "avatars", "review"];
function readInitialTab(): AiTabKey {
  if (typeof window === "undefined") return "overview";
  const t = new URLSearchParams(window.location.search).get("tab") as AiTabKey | null;
  return t && VALID_TABS.includes(t) ? t : "overview";
}


type AiItem = {
  id: string;
  media_type: "photo" | "video";
  upload_status: string;
  created_at: string;
  uploaded_at: string | null;
  has_storage: boolean;
  has_thumb: boolean;
  thumb_url: string | null;
  uploader_id: string;
  uploader_name: string | null;
  has_gps: boolean;
  is_seed: boolean;
  ai_status: string | null;
  ai_error: string | null;
  ai_processed_at: string | null;
  ai_caption: string | null;
  ai_scene_summary: string | null;
  ai_tags: string[] | null;
  ai_category: string | null;
  ai_activity: string | null;
  ai_quality: string | null;
  ai_is_highlight: boolean | null;
  ai_travel_moment: string | null;
  ai_story_value: string | null;
  ai_location_clues: string[] | null;
  ai_visual_priority: number | null;
  ai_quality_score: number | null;
  ai_privacy_flags: string[] | null;
  ai_contains_people: boolean | null;
  ai_contains_text: boolean | null;
  ai_analysis_level: string | null;
};

type FilterKey =
  | "all"
  | "pending"
  | "complete"
  | "errors"
  | "highlights"
  | "low_quality"
  | "seed"
  | "missing_thumb"
  | "missing_gps";

type SortKey = "newest" | "oldest" | "highlights" | "errors" | "score_desc" | "quality_asc";

function statusBadge(status: string | null) {
  const map: Record<string, StatusChipVariant> = {
    pending: "neutral",
    processing: "info",
    complete: "success",
    error: "danger",
  };
  const variant = map[status ?? ""] ?? "neutral";
  return <StatusChip variant={variant}>{status ?? "—"}</StatusChip>;
}

function fmt(ts: string | null) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function relTime(ts: string | null): string {
  if (!ts) return "—";
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function isLowQuality(it: AiItem): boolean {
  if (it.ai_quality === "low") return true;
  if (it.ai_quality_score != null && it.ai_quality_score <= 3) return true;
  return false;
}

function needsRecheck(it: AiItem): boolean {
  if (it.media_type === "video") return false;
  if (it.ai_status !== "complete") return false;
  if (!it.ai_caption) return true;
  if (it.ai_analysis_level && it.ai_analysis_level !== "high") return true;
  if (!it.ai_tags || it.ai_tags.length === 0) return true;
  return false;
}

function AiDebugPage() {
  const tripFn = useServerFn(getMyTrip);
  const listFn = useServerFn(getRecentTripMediaForAi);
  const reanalyzeFn = useServerFn(reanalyzeMedia);
  const qc = useQueryClient();
  const [limit, setLimit] = useState(25);
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<FilterKey>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [tab, setTab] = useState<AiTabKey>(readInitialTab);

  // Reflect tab in URL without router-level changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("tab") !== tab) {
      url.searchParams.set("tab", tab);
      window.history.replaceState({}, "", url.toString());
    }
  }, [tab]);


  const { data: tripData } = useQuery({
    queryKey: ["my-trip"],
    queryFn: () => tripFn({}),
  });
  const tripId = tripData?.trip?.id ?? null;

  const listKey = ["admin-ai-debug", tripId, limit] as const;
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: listKey,
    enabled: !!tripId,
    queryFn: () => listFn({ data: { tripId: tripId!, limit } }),
  });

  const items: AiItem[] = (data?.items as AiItem[] | undefined) ?? [];

  const counts = useMemo(() => {
    let pending = 0,
      complete = 0,
      errors = 0,
      highlights = 0,
      lowQuality = 0,
      recheck = 0;
    for (const it of items) {
      const s = it.ai_status ?? "pending";
      if (it.media_type !== "video" && (s === "pending" || s === "processing")) pending++;
      if (s === "complete") complete++;
      if (s === "error") errors++;
      if (it.ai_is_highlight) highlights++;
      if (isLowQuality(it)) lowQuality++;
      if (needsRecheck(it)) recheck++;
    }
    return { pending, complete, errors, highlights, lowQuality, recheck };
  }, [items]);

  const filteredItems = useMemo(() => {
    const list = items.filter((it) => {
      const s = it.ai_status ?? "pending";
      switch (filter) {
        case "all":
          return true;
        case "pending":
          return it.media_type !== "video" && (s === "pending" || s === "processing");
        case "complete":
          return s === "complete";
        case "errors":
          return s === "error";
        case "highlights":
          return !!it.ai_is_highlight;
        case "low_quality":
          return isLowQuality(it);
        case "seed":
          return it.is_seed;
        case "missing_thumb":
          return !it.has_thumb;
        case "missing_gps":
          return !it.has_gps;
      }
    });

    const sorted = [...list];
    const ts = (s: string | null) => (s ? new Date(s).getTime() : 0);
    switch (sort) {
      case "newest":
        sorted.sort((a, b) => ts(b.created_at) - ts(a.created_at));
        break;
      case "oldest":
        sorted.sort((a, b) => ts(a.created_at) - ts(b.created_at));
        break;
      case "highlights":
        sorted.sort((a, b) => Number(!!b.ai_is_highlight) - Number(!!a.ai_is_highlight) || ts(b.created_at) - ts(a.created_at));
        break;
      case "errors":
        sorted.sort(
          (a, b) =>
            Number(a.ai_status === "error" ? 0 : 1) - Number(b.ai_status === "error" ? 0 : 1) ||
            ts(b.created_at) - ts(a.created_at),
        );
        break;
      case "score_desc":
        sorted.sort((a, b) => (b.ai_visual_priority ?? -1) - (a.ai_visual_priority ?? -1));
        break;
      case "quality_asc":
        sorted.sort((a, b) => (a.ai_quality_score ?? 99) - (b.ai_quality_score ?? 99));
        break;
    }
    return sorted;
  }, [items, filter, sort]);

  const reanalyze = useMutation({
    mutationFn: (mediaId: string) => reanalyzeFn({ data: { mediaId } }),
    onSuccess: () => {
      toast.success("Re-analysis triggered");
      qc.invalidateQueries({ queryKey: ["admin-ai-debug"] });
    },
    onError: (e: any) => toast.error(`Re-analyze failed: ${e?.message ?? e}`),
  });

  const summaryChips: Array<{ key: FilterKey; label: string; n: number; tone: string }> = [
    { key: "pending", label: "Pending", n: counts.pending, tone: "bg-slate-500/15 text-slate-300 border-slate-500/30" },
    { key: "complete", label: "Complete", n: counts.complete, tone: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    { key: "errors", label: "Errors", n: counts.errors, tone: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
    { key: "highlights", label: "Highlights", n: counts.highlights, tone: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    { key: "low_quality", label: "Low quality", n: counts.lowQuality, tone: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
    { key: "all", label: "Needs recheck", n: counts.recheck, tone: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  ];

  const filterChips: Array<[FilterKey, string]> = [
    ["all", `All (${items.length})`],
    ["pending", `Pending (${counts.pending})`],
    ["complete", `Complete (${counts.complete})`],
    ["errors", `Errors (${counts.errors})`],
    ["highlights", `Highlights (${counts.highlights})`],
    ["low_quality", `Low quality (${counts.lowQuality})`],
    ["seed", `Demo/seed (${items.filter((i) => i.is_seed).length})`],
    ["missing_thumb", `Missing thumbnail (${items.filter((i) => !i.has_thumb).length})`],
    ["missing_gps", `Missing GPS (${items.filter((i) => !i.has_gps).length})`],
  ];

  return (
    <main className="mx-auto max-w-5xl px-5 md:px-8 py-6 md:py-8 space-y-6 pb-nav-safe">
      <PageHeader
        title="AI photo analysis"
        subtitle="Admin-only. Control AI on/off, pick travelers and dates, start or stop runs, and review per-photo results below."
      />

      <AiAdminTabs value={tab} onChange={setTab} />

      <div hidden={tab !== "overview"} className={tab === "overview" ? "" : "hidden"}>
        <AiOverviewSection tripId={tripId} />
      </div>

      <div hidden={tab !== "run"} className={tab === "run" ? "" : "hidden"}>
        <AiControlCenter tripId={tripId} />
      </div>

      <div hidden={tab !== "providers"} className={tab === "providers" ? "" : "hidden"}>
        <AdminAiAdvancedControls tripId={tripId} sections={["providers", "photoControls", "usageTable"]} />
      </div>

      <div hidden={tab !== "avatars"} className={tab === "avatars" ? "" : "hidden"}>
        <AdminAiAdvancedControls tripId={tripId} sections={["avatar", "avatarByTraveler"]} />
      </div>

      <div hidden={tab !== "review"} className={tab === "review" ? "space-y-6" : "hidden"}>
        {/* Summary chips */}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {summaryChips.map((c) => (
          <button
            key={c.label}
            type="button"
            onClick={() => setFilter(c.key)}
            className={`text-left rounded-2xl border px-3 py-2.5 transition-colors hover:brightness-110 ${c.tone}`}
          >
            <div className="text-[10px] uppercase tracking-wide opacity-80">{c.label}</div>
            <div className="text-xl font-semibold tabular-nums leading-tight">{c.n}</div>
          </button>
        ))}
      </div>

      <RoavaCard flush className="p-5">
        <div className="flex flex-wrap items-center gap-3">
          <StatusChip variant="neutral">{tripData?.trip?.name ?? "No active trip"}</StatusChip>
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto w-full sm:w-auto">
            <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
              <SelectTrigger className="w-[150px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
                <SelectItem value="highlights">Highlights first</SelectItem>
                <SelectItem value="errors">Errors first</SelectItem>
                <SelectItem value="score_desc">Highest score</SelectItem>
                <SelectItem value="quality_asc">Lowest quality</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
              <SelectTrigger className="w-[100px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 rows</SelectItem>
                <SelectItem value="25">25 rows</SelectItem>
                <SelectItem value="50">50 rows</SelectItem>
                <SelectItem value="100">100 rows</SelectItem>
              </SelectContent>
            </Select>
            <SecondaryButton
              size="sm"
              onClick={() => refetch()}
              disabled={!tripId || isFetching}
              className="h-8"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </SecondaryButton>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => {
                const allOpen = filteredItems.length > 0 && filteredItems.every((it) => openRows[it.id]);
                if (allOpen) {
                  setOpenRows({});
                } else {
                  const next: Record<string, boolean> = { ...openRows };
                  for (const it of filteredItems) next[it.id] = true;
                  setOpenRows(next);
                }
              }}
              disabled={filteredItems.length === 0}
            >
              {filteredItems.length > 0 && filteredItems.every((it) => openRows[it.id]) ? "Collapse all" : "Expand all"}
            </Button>
          </div>
          <div className="basis-full flex flex-wrap gap-1.5">
            {filterChips.map(([key, label]) => {
              const active = filter === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-[var(--roava-blue)] text-[var(--roava-blue-foreground)] border-[var(--roava-blue)]"
                      : "bg-[var(--roava-card)] text-[var(--roava-text-muted)] border-[var(--roava-border)] hover:text-[var(--roava-text)]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </RoavaCard>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !tripId ? (
        <p className="text-sm text-muted-foreground">No active trip.</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No media yet. Upload a photo to test.</p>
      ) : filteredItems.length === 0 ? (
        <p className="text-sm text-muted-foreground">No rows match this filter.</p>
      ) : (
        <div className="space-y-2.5">
          {filteredItems.map((it) => {
            const isVideo = it.media_type === "video";
            const isOpen = !!openRows[it.id];
            const status = it.ai_status ?? "pending";
            const low = isLowQuality(it);
            const recheck = needsRecheck(it);

            const cueClasses: string[] = ["rounded-[20px] border bg-[var(--roava-card)]"];
            if (status === "error") cueClasses.push("border-rose-500/40 ring-1 ring-rose-500/30");
            else if (it.ai_is_highlight) cueClasses.push("border-amber-500/40 ring-1 ring-amber-500/30");
            else if (status === "complete") cueClasses.push("border-emerald-500/30 border-l-4 border-l-emerald-500/70");
            else cueClasses.push("border-[var(--roava-border)]");
            if (low || it.is_seed) cueClasses.push("opacity-80");

            return (
              <Card key={it.id} className={cueClasses.join(" ")}>
                <Collapsible
                  open={isOpen}
                  onOpenChange={(o) => setOpenRows((p) => ({ ...p, [it.id]: o }))}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="w-full text-left px-3 py-3 sm:px-4 flex items-start gap-3 hover:bg-muted/30 transition-colors rounded-[20px]"
                    >
                      {/* Thumbnail */}
                      <div className="relative h-14 w-14 shrink-0">
                        <div className="h-full w-full overflow-hidden rounded-xl bg-muted">
                          {it.thumb_url ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={it.thumb_url}
                              alt=""
                              loading="lazy"
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.display = "none";
                              }}
                            />
                          ) : isVideo ? (
                            <Film className="h-5 w-5 m-auto mt-[18px] text-muted-foreground" />
                          ) : (
                            <ImageOff className="h-5 w-5 m-auto mt-[18px] text-muted-foreground" />
                          )}
                        </div>
                        {it.ai_is_highlight ? (
                          <span className="absolute -top-1.5 -right-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-white shadow ring-2 ring-background">
                            <Star className="h-3 w-3 fill-white" />
                          </span>
                        ) : null}
                      </div>


                      {/* Right column */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                          <span className="font-medium text-foreground truncate max-w-[180px]">
                            {it.uploader_name ?? "Unknown traveler"}
                          </span>
                          <code className="text-[10px] text-muted-foreground">{it.id.slice(0, 8)}…</code>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(it.id);
                              toast.success("Copied media id");
                            }}
                            className="text-muted-foreground hover:text-foreground inline-flex"
                            title="Copy full media id"
                          >
                            <Copy className="h-3 w-3" />
                          </span>
                          <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
                            {relTime(it.uploaded_at ?? it.created_at)}
                            {it.ai_processed_at ? <> · ai {relTime(it.ai_processed_at)}</> : null}
                          </span>
                        </div>

                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                          {isVideo ? (
                            <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
                              video (skipped)
                            </Badge>
                          ) : (
                            statusBadge(status)
                          )}
                          {it.ai_is_highlight ? (
                            <Badge variant="outline" className="text-[10px] bg-amber-500/15 text-amber-300 border-amber-500/30">
                              highlight
                            </Badge>
                          ) : null}
                          {recheck ? (
                            <Badge variant="outline" className="text-[10px] bg-sky-500/10 text-sky-300 border-sky-500/30">
                              recheck
                            </Badge>
                          ) : null}
                          {it.ai_quality ? (
                            <Badge variant="outline" className={`text-[10px] ${low ? "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" : ""}`}>
                              {it.ai_quality}
                            </Badge>
                          ) : null}
                          {it.ai_category || it.ai_activity ? (
                            <span className="text-[11px] text-muted-foreground truncate">
                              {[it.ai_category, it.ai_activity].filter(Boolean).join(" · ")}
                            </span>
                          ) : null}
                          {it.ai_visual_priority != null ? (
                            <Badge variant="outline" className="text-[10px]">
                              p{it.ai_visual_priority}
                            </Badge>
                          ) : null}
                          {it.ai_quality_score != null ? (
                            <Badge variant="outline" className="text-[10px]">
                              q{it.ai_quality_score}
                            </Badge>
                          ) : null}
                          {it.is_seed ? (
                            <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
                              seed
                            </Badge>
                          ) : null}
                          {!it.has_gps ? (
                            <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
                              no GPS
                            </Badge>
                          ) : null}
                          {!it.has_thumb ? (
                            <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">
                              no thumb
                            </Badge>
                          ) : null}
                        </div>

                        {it.ai_caption ? (
                          <p className="mt-1.5 text-[12.5px] text-foreground/90 line-clamp-1">{it.ai_caption}</p>
                        ) : status === "error" && it.ai_error ? (
                          <p className="mt-1.5 text-[12px] text-rose-300/90 line-clamp-1 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> {it.ai_error}
                          </p>
                        ) : null}
                      </div>

                      <ChevronDown
                        className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-5 space-y-3">
                      <div className="text-[11px] text-muted-foreground">
                        created {fmt(it.created_at)} · uploaded {fmt(it.uploaded_at)}
                        {it.ai_processed_at ? <> · processed {fmt(it.ai_processed_at)}</> : null}
                      </div>

                      {!isVideo && it.ai_error ? (
                        <div className="text-[12px] rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-300 px-3 py-2">
                          <span className="font-medium">ai_error:</span> {it.ai_error}
                        </div>
                      ) : null}

                      {!isVideo && it.ai_scene_summary ? (
                        <div className="text-[13px]">
                          <span className="text-muted-foreground text-[11px] uppercase tracking-wide">scene summary</span>
                          <p className="mt-0.5">{it.ai_scene_summary}</p>
                        </div>
                      ) : null}

                      {!isVideo && it.ai_story_value ? (
                        <div className="text-[13px]">
                          <span className="text-muted-foreground text-[11px] uppercase tracking-wide">story value</span>
                          <p className="mt-0.5">{it.ai_story_value}</p>
                        </div>
                      ) : null}

                      {!isVideo && it.ai_tags && it.ai_tags.filter((t) => t && t.toLowerCase() !== "seed").length > 0 ? (
                        <div>
                          <div className="text-muted-foreground text-[11px] uppercase tracking-wide mb-1">tags</div>
                          <div className="flex flex-wrap gap-1.5">
                            {it.ai_tags.filter((t) => t && t.toLowerCase() !== "seed").map((t) => (
                              <Badge key={t} variant="secondary" className="text-[10px]">
                                {t}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {!isVideo && it.ai_location_clues && it.ai_location_clues.length > 0 ? (
                        <div>
                          <div className="text-muted-foreground text-[11px] uppercase tracking-wide mb-1">location clues</div>
                          <div className="flex flex-wrap gap-1.5">
                            {it.ai_location_clues.map((c) => (
                              <Badge key={c} variant="outline" className="text-[10px]">
                                {c}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
                        <Field label="storage" value={it.has_storage ? "ok" : "missing"} bad={!it.has_storage} />
                        <Field label="thumb" value={it.has_thumb ? "ok" : "missing"} muted={!it.has_thumb} />
                        <Field label="gps" value={it.has_gps ? "ok" : "missing"} muted={!it.has_gps} />
                        <Field label="upload" value={it.upload_status} />
                        <Field label="ai_category" value={isVideo ? "—" : it.ai_category ?? "—"} />
                        <Field label="ai_activity" value={isVideo ? "—" : it.ai_activity ?? "—"} />
                        <Field label="ai_quality" value={isVideo ? "—" : it.ai_quality ?? "—"} />
                        <Field label="analysis" value={isVideo ? "—" : it.ai_analysis_level ?? "—"} />
                      </div>

                      {!isVideo && it.ai_privacy_flags && it.ai_privacy_flags.length > 0 ? (
                        <div>
                          <div className="text-muted-foreground text-[11px] uppercase tracking-wide mb-1">privacy flags</div>
                          <div className="flex flex-wrap gap-1.5">
                            {it.ai_privacy_flags.map((p) => (
                              <Badge key={p} variant="outline" className="text-[10px] bg-amber-500/10 text-amber-300 border-amber-500/30">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      {!isVideo ? (
                        <div className="flex justify-end">
                          <SecondaryButton
                            size="sm"
                            className="h-8"
                            disabled={reanalyze.isPending}
                            onClick={() => reanalyze.mutate(it.id)}
                          >
                            <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                            Re-analyze with AI
                          </SecondaryButton>
                        </div>
                      ) : null}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}
      </div>
    </main>

  );
}

function Field({
  label,
  value,
  bad,
  muted,
}: {
  label: string;
  value: string;
  bad?: boolean;
  muted?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={
          bad ? "text-rose-400" : muted ? "text-muted-foreground" : "text-foreground"
        }
      >
        {value}
      </div>
    </div>
  );
}
